import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { withModal, InlineWaiting } from "components/ui";
import { Menu, MenuItem } from "components/ui/menu";
import {
  textPatient,
  textConsentPatient,
  pushNotifyPatient,
  textKudosPatient,
  sendPatientMaskFittingInstructions
} from "actions/patient";
import ContactRecord from "components/profile/modals/contact-record";
import SpecialInstructions from "components/profile/modals/special-instructions";
import WalkInOrder from "components/profile/modals/walk-in-order";
import PatientEmailMenu from "components/profile/email-menu";
import { useDispatch } from "react-redux";
import ModalLink from "components/shared/modal-link";
import HasAppPermission from "components/shared/has-app-permission";
import MdKeyboardArrowRight from "react-icons/lib/md/keyboard-arrow-right";
import HasRolePermission from "components/shared/has-role-permission";
import SelectOrderProductLineModal from "components/profile/modals/select-order-product-line";

const ProfileMenu = ({
  patientInfo,
  patientId,
  onCreateOrder,
  openModal,
  closeModal,
  disabled
}) => {
  const dispatch = useDispatch();
  const [ isFetching, setIsFetching ] = useState(true);
  useEffect(() => {
    if (patientInfo) {
      setIsFetching(false);
    }
  }, [patientInfo?.stage_id])

  let textSublist = [
    {
      label: "Request Text Consent",
      patientStages: [1, 2, 4],
      order: 1,
      onClick: () => {
        dispatch(
          textConsentPatient({
            patientId: patientId,
            phone: "Primary"
          })
        ).then(() => {
          dispatch(
            textConsentPatient({
              patientId: patientId,
              phone: "Secondary"
            })
          )
        })
      }
    },
    // 2: Welcome to the Resupply Program
    {
      label: "Reorder Supplies",
      patientStages: [1],
      order: 3,
      onClick: () => {
        dispatch(textPatient({ patientId }));
      }
    },
    {
      label: "Kudos",
      patientStages: [2],
      order: 4,
      onClick: () => {
        dispatch(textKudosPatient({ patientId }));
      }
    }
  ]?.filter(x => x.patientStages.includes(patientInfo?.stage_id))?.sort((a, b) => a.order - b.order);

  let pushSubList = [
    {
      label: "Send Push Notification",
      patientStages: [1, 2, 4],
      order: 1,
      onClick: () => {
        dispatch(pushNotifyPatient({ patientId }));
      }
    }
  ]?.filter(x => x.patientStages.includes(patientInfo?.stage_id))?.sort((a, b) => a.order - b.order);

  const patient_product_lines = patientInfo?.patient_product_lines || [];

  return(
    <>
      {isFetching ? (
        <InlineWaiting reason="Fetching patient info" />
      ) : (
        <div>
        <Menu>
          <HasAppPermission permission="orderSupplies">
            <MenuItem
              disabled={disabled}
              onClick={() => {
                if (patient_product_lines.length == 0) {
                  onCreateOrder("CPAP");
                }
                else if (patient_product_lines.length == 1) {
                  onCreateOrder(patient_product_lines[0]?.text);
                } else {
                  openModal(
                    <SelectOrderProductLineModal
                      closeSelf={closeModal}
                      patient_product_lines={patient_product_lines}
                      patientInfo={patientInfo}
                      onCreateOrder={onCreateOrder}
                    />
                  );
                }
              }}
            >
              Create Order
            </MenuItem>
            <MenuItem
              disabled={disabled}
              onClick={() =>
                openModal(
                  <WalkInOrder closeSelf={closeModal} patientId={patientId} />
                )
              }
            >
              Walk-in Order
            </MenuItem>
          </HasAppPermission>
          <ModalLink disabled={disabled} to={`/patients/${patientId}/edit`}>
            <MenuItem disabled={disabled}>Edit Profile</MenuItem>
          </ModalLink>
          <MenuItem
            disabled={disabled}
            onClick={() =>
              openModal(
                <SpecialInstructions
                  patientId={patientId}
                  closeSelf={closeModal}
                />
              )
            }
          >
            Add Special Instructions
          </MenuItem>
          <HasAppPermission permission="orderSupplies">
            <PatientEmailMenu
              patientId={patientId}
              openModal={openModal}
              closeModal={closeModal}
              patientEmail={patientInfo.email}
              patientStageId={patientInfo?.stage_id}
            />
            <MenuItem disabled={disabled} sublist={textSublist}>
              Text Message
              <span className="menu-icon">
                <MdKeyboardArrowRight />
              </span>
            </MenuItem>
            <MenuItem disabled={disabled} sublist={pushSubList}>
              Push Notification
              <span className="menu-icon">
                <MdKeyboardArrowRight />
              </span>
            </MenuItem>
          </HasAppPermission>
          {patientInfo && patientInfo.autofit_eligible && (
            <HasAppPermission permission="maskFitting">
              <MenuItem
                onClick={() => {
                  dispatch(sendPatientMaskFittingInstructions(patientId));
                }}
              >
                Send Mask Fitting Instructions
              </MenuItem>
            </HasAppPermission>
          )}
          <HasRolePermission
            // DEV-15262 Admins and SAAS users only
            allowedRoles={["Administrator", "ServiceAdmin", "ServiceCoach"]}
          >
            <MenuItem
              onClick={() =>
                openModal(
                  <ContactRecord patientId={patientId} closeSelf={closeModal} />
                )
              }
            >
              Create Contact Record
            </MenuItem>
          </HasRolePermission>
        </Menu>
      </div>
      )}
    </>
  )
};

ProfileMenu.propTypes = {
  patientId: PropTypes.string.isRequired,
  patientInfo: PropTypes.object.isRequired,
  onCreateOrder: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired
};

ProfileMenu.defaultProps = {
  disabled: false
};

export default withModal(ProfileMenu);
